import React, { useEffect, useState } from 'react';
import {
  Table,
  Card,
  Typography,
  Tag,
  Space,
  Button,
  message,
  Modal,
  Form,
  Select,
  Row,
  Col,
  Popconfirm,
} from 'antd';
import { ReloadOutlined, PlusOutlined, EyeOutlined, CheckCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import dayjs from 'dayjs';
import TextArea from 'antd/es/input/TextArea';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
};

const { Title, Text } = Typography;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const FiltersContainer = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-body {
    padding: 20px !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .ant-card-body {
      padding: 12px !important;
    }
  }
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }
`;

const MerchantCriticalNotifications: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [severityFilter, setSeverityFilter] = useState<string>(searchParams.get('severity') || '');
  const [bugStatusFilter, setBugStatusFilter] = useState<string>(searchParams.get('bugStatus') || '');
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || '');
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<any>(null);
  const [form] = Form.useForm();

  const pageId = parseInt(searchParams.get('pageId') || '1');
  const perPage = parseInt(searchParams.get('perPage') || '10');

  useEffect(() => {
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, perPage, severityFilter, bugStatusFilter, statusFilter]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const params: any = {
        pageId,
        perPage,
      };
      if (severityFilter) {
        params.severity = severityFilter;
      }
      if (bugStatusFilter) {
        params.bugStatus = bugStatusFilter;
      }
      if (statusFilter) {
        params.status = statusFilter;
      }

      const response = await apiClient.get(
        API_ENDPOINTS.MERCHANT_GET_CRITICAL_NOTIFICATIONS,
        { params }
      );

      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        setNotifications(data.itemsList || []);
        const paginator = data.paginator || data.pagination;
        if (paginator) {
          setPagination({
            current: paginator.currentPage || pageId,
            pageSize: paginator.perPage || perPage,
            total:
              paginator.itemCount ||
              paginator.totalCount ||
              paginator.total ||
              (data.itemsList || []).length,
          });
        }
      }
    } catch (error: any) {
      console.error('Error fetching merchant critical notifications:', error);
      message.error(
        error.response?.data?.message ||
          'Failed to fetch critical notifications'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination: any) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('pageId', newPagination.current.toString());
    newParams.set('perPage', newPagination.pageSize.toString());
    setSearchParams(newParams);
  };

  const updateURLParams = (updates: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    newParams.set('pageId', '1');
    setSearchParams(newParams);
  };

  const handleFilterChange = (key: string, value: string | null) => {
    updateURLParams({ [key]: value });
    if (key === 'severity') setSeverityFilter(value || '');
    if (key === 'bugStatus') setBugStatusFilter(value || '');
    if (key === 'status') setStatusFilter(value || '');
  };

  const handleCreate = () => {
    form.resetFields();
    setCreateModalVisible(true);
  };

  const handleCreateSubmit = async (values: any) => {
    try {
      await apiClient.post(
        API_ENDPOINTS.MERCHANT_CREATE_CRITICAL_NOTIFICATION,
        {
          severity: values.severity,
          subject: values.subject,
          details: values.details,
        }
      );
      message.success('Notification created successfully');
      setCreateModalVisible(false);
      form.resetFields();
      fetchNotifications();
    } catch (error: any) {
      message.error(
        error.response?.data?.message || 'Failed to create notification'
      );
    }
  };

  const handleMarkAsSeen = async (id: string) => {
    try {
      await apiClient.patch(
        `${API_ENDPOINTS.MERCHANT_MARK_NOTIFICATION_AS_SEEN}/${id}/mark-seen`
      );
      message.success('Notification marked as seen');
      fetchNotifications();
    } catch (error: any) {
      message.error(
        error.response?.data?.message || 'Failed to mark notification as seen'
      );
    }
  };

  const handleMarkAllAsSeen = async () => {
    try {
      await apiClient.patch(
        API_ENDPOINTS.MERCHANT_MARK_ALL_NOTIFICATIONS_AS_SEEN
      );
      message.success('All notifications marked as seen');
      fetchNotifications();
    } catch (error: any) {
      message.error(
        error.response?.data?.message || 'Failed to mark all notifications as seen'
      );
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await apiClient.delete(
        API_ENDPOINTS.MERCHANT_DELETE_CRITICAL_NOTIFICATION.replace('{id}', id)
      );
      message.success('Notification deleted successfully');
      fetchNotifications();
    } catch (error: any) {
      message.error(
        error.response?.data?.message || 'Failed to delete notification'
      );
    }
  };

  const handleDeleteAll = async () => {
    try {
      // Delete all notifications for this merchant company
      const response = await apiClient.delete(
        API_ENDPOINTS.MERCHANT_DELETE_ALL_NOTIFICATIONS
      );
      
      if (response.data?.status === 'success') {
        const deletedCount = response.data?.data?.deletedCount || 0;
        message.success(`Deleted ${deletedCount} notification(s)`);
        fetchNotifications();
      } else {
        message.error(response.data?.message || 'Failed to delete all notifications');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete all notifications');
    }
  };

  const columns = [
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      render: (severity: string) => {
        const colorMap: Record<string, string> = {
          CRITICAL: 'red',
          HIGH: 'orange',
          LOW: 'blue',
        };
        return <Tag color={colorMap[severity] || 'default'}>{severity}</Tag>;
      },
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
      render: (subject: string, record: any) => (
        <Text 
          strong 
          style={{ 
            color: COLORS.textPrimary,
            cursor: 'pointer',
            textDecoration: 'underline'
          }}
          onClick={() => {
            setSelectedNotification(record);
            setDetailModalVisible(true);
          }}
        >
          {subject}
        </Text>
      ),
    },
    {
      title: 'Details',
      dataIndex: 'details',
      key: 'details',
      ellipsis: true,
      render: (details: string, record: any) => (
        <Space>
          <Text 
            style={{ 
              color: COLORS.textSecondary,
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
            onClick={() => {
              setSelectedNotification(record);
              setDetailModalVisible(true);
            }}
          >
            {details}
          </Text>
          {(details?.length > 100 || record.subject?.length > 50) && (
            <Button
              type="link"
              size="small"
              onClick={() => {
                setSelectedNotification(record);
                setDetailModalVisible(true);
              }}
            >
              View Details
            </Button>
          )}
        </Space>
      ),
    },
    {
      title: 'Bug Status',
      dataIndex: 'bugStatus',
      key: 'bugStatus',
      render: (status: string) => {
        const colorMap: Record<string, string> = {
          FIXED: 'green',
          FIXING: 'blue',
          ESCALATED: 'orange',
          CUSTOMER: 'purple',
          PENDING: 'default',
        };
        return <Tag color={colorMap[status] || 'default'}>{status}</Tag>;
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: string) =>
        value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right' as const,
      width: 200,
      render: (_: any, record: any) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedNotification(record);
              setDetailModalVisible(true);
            }}
            size="small"
          >
            View
          </Button>
          {!record.isSeen && (
            <Button
              icon={<CheckCircleOutlined />}
              onClick={() => handleMarkAsSeen(record.id)}
              size="small"
              type="primary"
            >
              Mark Seen
            </Button>
          )}
          <Popconfirm
            title={
              <div>
                <div style={{ marginBottom: 8, fontWeight: 500 }}>Delete notification?</div>
                <div style={{ fontSize: '12px', color: '#999' }}>Are you sure you want to delete this notification? This action cannot be undone.</div>
              </div>
            }
            onConfirm={() => handleDelete(record.id)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button
              icon={<DeleteOutlined />}
              size="small"
              danger
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
          Critical Notifications
        </Title>
        <Space>
          <Button
            onClick={() =>
              navigate('/igwena/critical-notifications/account-update-requests')
            }
          >
            Account Modify Requests
          </Button>
          {notifications.length > 0 && (
            <>
              <Button
                icon={<CheckCircleOutlined />}
                onClick={handleMarkAllAsSeen}
                type="default"
              >
                Mark All as Seen ({notifications.filter((n: any) => !n.isSeen).length})
              </Button>
              <Popconfirm
                title={
                  <div>
                    <div style={{ marginBottom: 8, fontWeight: 500 }}>Delete all notifications?</div>
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      This will soft-delete every notification for your company.
                    </div>
                  </div>
                }
                onConfirm={handleDeleteAll}
                okText="Yes, Delete All"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
              >
                <Button
                  icon={<DeleteOutlined />}
                  danger
                  type="default"
                >
                  Delete All Notifications
                </Button>
              </Popconfirm>
            </>
          )}
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchNotifications}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            New Notification
          </Button>
        </Space>
      </HeaderContainer>

      <FiltersContainer>
        <Space size="large" wrap>
          <Space direction="vertical" size={4}>
            <Text style={{ color: COLORS.textSecondary }}>Severity</Text>
            <Select
              allowClear
              placeholder="All"
              value={severityFilter || undefined}
              onChange={(value) => handleFilterChange('severity', value || null)}
              style={{ width: 160 }}
            >
              <Select.Option value="CRITICAL">Critical</Select.Option>
              <Select.Option value="HIGH">High</Select.Option>
              <Select.Option value="LOW">Low</Select.Option>
            </Select>
          </Space>
          <Space direction="vertical" size={4}>
            <Text style={{ color: COLORS.textSecondary }}>Bug Status</Text>
            <Select
              allowClear
              placeholder="All"
              value={bugStatusFilter || undefined}
              onChange={(value) =>
                handleFilterChange('bugStatus', value || null)
              }
              style={{ width: 200 }}
            >
              <Select.Option value="PENDING">Pending</Select.Option>
              <Select.Option value="FIXING">Fixing</Select.Option>
              <Select.Option value="FIXED">Fixed</Select.Option>
              <Select.Option value="ESCALATED">Escalated</Select.Option>
              <Select.Option value="CUSTOMER">Customer</Select.Option>
            </Select>
          </Space>
        </Space>
      </FiltersContainer>

      <Card
        style={{
          background: COLORS.bgSecondary,
          border: `1px solid ${COLORS.border}`,
        }}
      >
        <div style={{ marginBottom: 12, color: COLORS.textSecondary }}>
          Total {pagination.total} notifications
        </div>
        <TableScrollWrapper>
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={notifications}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: window.innerWidth > 768,
              showTotal: (total, range) => (
                <span style={{ color: COLORS.textSecondary }}>
                  Showing {range[0]}-{range[1]} of {total} notifications
                </span>
              ),
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            style={{ background: 'transparent' }}
            size="small"
            scroll={{ x: 'max-content' }}
            components={{
              body: {
                cell: (props: any) => (
                  <td
                    {...props}
                    style={{
                      ...props.style,
                    background: COLORS.bgSecondary,
                    borderColor: COLORS.border,
                  }}
                />
              ),
            },
          }}
          />
        </TableScrollWrapper>
      </Card>

      <Modal
        title="New Critical Notification"
        open={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        onOk={() => form.submit()}
        okText="Submit"
        okButtonProps={{ type: 'primary' }}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateSubmit}>
          <Form.Item
            name="severity"
            label="Severity"
            rules={[{ required: true, message: 'Please select severity' }]}
          >
            <Select placeholder="Select severity">
              <Select.Option value="CRITICAL">Critical</Select.Option>
              <Select.Option value="HIGH">High</Select.Option>
              <Select.Option value="LOW">Low</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="subject"
            label="Subject"
            rules={[{ required: true, message: 'Please enter subject' }]}
          >
            <TextArea rows={2} placeholder="Short title for this issue" />
          </Form.Item>
          <Form.Item
            name="details"
            label="Details"
            rules={[{ required: true, message: 'Please describe the issue' }]}
          >
            <TextArea
              rows={5}
              placeholder="Provide as much detail as possible so the admin team can act quickly"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Details Modal */}
      <Modal
        title="Notification Details"
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedNotification(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setDetailModalVisible(false);
            setSelectedNotification(null);
          }}>
            Close
          </Button>,
          selectedNotification && !selectedNotification.isSeen && (
            <Button
              key="markSeen"
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => {
                handleMarkAsSeen(selectedNotification.id);
                setDetailModalVisible(false);
                setSelectedNotification(null);
              }}
            >
              Mark as Seen
            </Button>
          ),
        ]}
        width={800}
      >
        {selectedNotification && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
              <Text strong style={{ color: COLORS.textPrimary, fontSize: '16px' }}>
                Subject:
              </Text>
              <div style={{ marginTop: '8px', color: COLORS.textPrimary }}>
                {selectedNotification.subject}
              </div>
            </div>
            <div>
              <Text strong style={{ color: COLORS.textPrimary, fontSize: '16px' }}>
                Details:
              </Text>
              <div 
                style={{ 
                  marginTop: '8px', 
                  color: COLORS.textSecondary,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  padding: '12px',
                  background: COLORS.bgSecondary,
                  borderRadius: '4px',
                  border: `1px solid ${COLORS.border}`
                }}
              >
                {selectedNotification.details}
              </div>
            </div>
            {selectedNotification.actionKey && (
              <div>
                <Text strong style={{ color: COLORS.textPrimary, fontSize: '16px' }}>
                  Action Key:
                </Text>
                <div style={{ marginTop: '8px' }}>
                  <Tag color="blue">{selectedNotification.actionKey}</Tag>
                </div>
              </div>
            )}
            {selectedNotification.actionData && (
              <div>
                <Text strong style={{ color: COLORS.textPrimary, fontSize: '16px' }}>
                  Action Data:
                </Text>
                <div 
                  style={{ 
                    marginTop: '8px',
                    padding: '12px',
                    background: COLORS.bgSecondary,
                    borderRadius: '4px',
                    border: `1px solid ${COLORS.border}`,
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}
                >
                  <pre style={{ margin: 0, color: COLORS.textSecondary }}>
                    {JSON.stringify(selectedNotification.actionData, null, 2)}
                  </pre>
                </div>
              </div>
            )}
            <Row gutter={16}>
              <Col span={12}>
                <Text strong style={{ color: COLORS.textPrimary }}>Severity: </Text>
                <Tag color={
                  selectedNotification.severity === 'CRITICAL' ? 'red' :
                  selectedNotification.severity === 'HIGH' ? 'orange' : 'blue'
                }>
                  {selectedNotification.severity}
                </Tag>
              </Col>
              <Col span={12}>
                <Text strong style={{ color: COLORS.textPrimary }}>Bug Status: </Text>
                <Tag color={
                  selectedNotification.bugStatus === 'FIXED' ? 'green' :
                  selectedNotification.bugStatus === 'FIXING' ? 'blue' :
                  selectedNotification.bugStatus === 'ESCALATED' ? 'orange' :
                  selectedNotification.bugStatus === 'CUSTOMER' ? 'purple' : 'default'
                }>
                  {selectedNotification.bugStatus}
                </Tag>
              </Col>
              <Col span={12}>
                <Text strong style={{ color: COLORS.textPrimary }}>Created: </Text>
                <Text style={{ color: COLORS.textSecondary }}>
                  {dayjs(selectedNotification.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                </Text>
              </Col>
            </Row>
          </Space>
        )}
      </Modal>
    </PageContainer>
  );
};

export default MerchantCriticalNotifications;


